import React, { useState } from "react";
import { Loader2, Search, PenTool } from "lucide-react";
import toast from "react-hot-toast";
import api from "../../utils/axios";

const hasText = (value) => typeof value === "string" && value.trim().length > 0;
const hasArrayValues = (value) => Array.isArray(value) && value.length > 0;
const toNumber = (value, fallback = 0) => {
  if (value === "" || value === null || value === undefined) return fallback;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const mergeImages = (currentImages = [], suggestedImages = []) => {
  const seen = new Set();
  return [...currentImages, ...suggestedImages].filter((item) => {
    if (!item?.url || seen.has(item.url)) return false;
    if (!/\.(jpg|jpeg|png|webp|gif|avif)(\?|$)/i.test(item.url)) return false;
    seen.add(item.url);
    return true;
  });
};

const scoreClasses = (score = 0) => {
  if (score >= 85) return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (score >= 70) return "bg-purple-50 text-purple-700 border-purple-200";
  if (score >= 50) return "bg-amber-50 text-amber-700 border-amber-200";
  return "bg-rose-50 text-rose-700 border-rose-200";
};

const mergeUniqueStrings = (current = [], next = []) =>
  Array.from(new Set([...(Array.isArray(current) ? current : []), ...(Array.isArray(next) ? next : [])].filter(Boolean)));

const mergeByKey = (current = [], incoming = [], keyBuilder, mergeItem) => {
  const merged = Array.isArray(current) ? [...current] : [];
  const indexByKey = new Map();

  merged.forEach((item, index) => indexByKey.set(keyBuilder(item, index), index));

  (Array.isArray(incoming) ? incoming : []).forEach((item, index) => {
    const key = keyBuilder(item, index);
    if (!key) {
      merged.push(item);
      return;
    }

    const existingIndex = indexByKey.get(key);
    if (existingIndex === undefined) {
      indexByKey.set(key, merged.length);
      merged.push(item);
      return;
    }

    merged[existingIndex] = mergeItem(merged[existingIndex], item);
  });

  return merged;
};

const mergeVenueSlots = (current = [], incoming = []) => {
  const fallbackSlots = [
    { slot: "morning", available: false },
    { slot: "afternoon", available: false },
    { slot: "evening", available: false },
    { slot: "full-day", available: false },
  ];
  const baseSlots = hasArrayValues(current) ? current : fallbackSlots;

  return mergeByKey(
    baseSlots,
    incoming,
    (slot) => slot?.slot,
    (existing, next) => ({
      ...existing,
      ...next,
      available:
        typeof next?.available === "boolean" ? next.available : existing.available,
    })
  );
};

const mergeAreas = (current = [], incoming = []) =>
  mergeByKey(
    current,
    (Array.isArray(incoming) ? incoming : []).filter(
      (area) =>
        hasText(area?.title) ||
        hasText(area?.description) ||
        Number(area?.seating) > 0 ||
        Number(area?.floating) > 0
    ),
    (area, index) =>
      `${String(area?.title || "").trim().toLowerCase()}::${String(
        area?.type || index
      ).trim().toLowerCase()}`,
    (existing, next) => ({
      ...existing,
      ...next,
      title: hasText(next?.title) ? next.title : existing.title,
      type: hasText(next?.type) ? next.type : existing.type,
      seating: toNumber(next?.seating, existing?.seating || 0),
      floating: toNumber(next?.floating, existing?.floating || 0),
      description: hasText(next?.description)
        ? next.description
        : existing?.description,
    })
  );

const mergePackageArray = (current = [], incoming = [], defaults = {}) =>
  mergeByKey(
    current,
    (Array.isArray(incoming) ? incoming : []).filter(
      (item) =>
        hasText(item?.packageName) ||
        hasText(item?.description) ||
        Number(item?.price) > 0
    ),
    (item, index) =>
      `${String(item?.packageName || "").trim().toLowerCase()}::${String(
        item?.type || item?.out_station || index
      ).trim().toLowerCase()}`,
    (existing, next) => ({
      ...defaults,
      ...existing,
      ...next,
      packageName: hasText(next?.packageName)
        ? next.packageName
        : existing?.packageName || "",
      description: hasText(next?.description)
        ? next.description
        : existing?.description || "",
      price: toNumber(next?.price, existing?.price || 0),
      type: hasText(next?.type) ? next.type : existing?.type,
      out_station: hasText(next?.out_station)
        ? next.out_station
        : existing?.out_station,
    })
  );

const mergeVenuePricing = (current = {}, incoming = {}) => ({
  ...current,
  ...incoming,
  model: hasText(incoming?.model) ? incoming.model : current?.model,
  perPax: {
    ...(current?.perPax || {}),
    ...(incoming?.perPax || {}),
    veg: toNumber(incoming?.perPax?.veg, current?.perPax?.veg || 0),
    nonVeg: toNumber(incoming?.perPax?.nonVeg, current?.perPax?.nonVeg || 0),
  },
  fixedBooking: {
    ...(current?.fixedBooking || {}),
    ...(incoming?.fixedBooking || {}),
    basePrice: toNumber(
      incoming?.fixedBooking?.basePrice,
      current?.fixedBooking?.basePrice || 0
    ),
    description: hasText(incoming?.fixedBooking?.description)
      ? incoming.fixedBooking.description
      : current?.fixedBooking?.description || "",
  },
  packagePerDay: {
    ...(current?.packagePerDay || {}),
    ...(incoming?.packagePerDay || {}),
    price: toNumber(
      incoming?.packagePerDay?.price,
      current?.packagePerDay?.price || 0
    ),
    noOfRooms: toNumber(
      incoming?.packagePerDay?.noOfRooms,
      current?.packagePerDay?.noOfRooms || 0
    ),
    noOfPax: toNumber(
      incoming?.packagePerDay?.noOfPax,
      current?.packagePerDay?.noOfPax || 0
    ),
    noOfMeals: toNumber(
      incoming?.packagePerDay?.noOfMeals,
      current?.packagePerDay?.noOfMeals || 0
    ),
  },
});

const mergeCatererPricing = (current = {}, incoming = {}) => ({
  ...current,
  ...incoming,
  model: hasText(incoming?.model) ? incoming.model : current?.model,
  perPlate: {
    ...(current?.perPlate || {}),
    ...(incoming?.perPlate || {}),
    veg: toNumber(incoming?.perPlate?.veg, current?.perPlate?.veg || 0),
    nonVeg: toNumber(incoming?.perPlate?.nonVeg, current?.perPlate?.nonVeg || 0),
    minPax: toNumber(incoming?.perPlate?.minPax, current?.perPlate?.minPax || 0),
  },
  packagePerDay: {
    ...(current?.packagePerDay || {}),
    ...(incoming?.packagePerDay || {}),
    veg: toNumber(
      incoming?.packagePerDay?.veg,
      current?.packagePerDay?.veg || 0
    ),
    nonVeg: toNumber(
      incoming?.packagePerDay?.nonVeg,
      current?.packagePerDay?.nonVeg || 0
    ),
    minPax: toNumber(
      incoming?.packagePerDay?.minPax,
      current?.packagePerDay?.minPax || 0
    ),
    price_starts: toNumber(
      incoming?.packagePerDay?.price_starts,
      current?.packagePerDay?.price_starts || 0
    ),
  },
});

const normalizeFoodType = (value) => {
  if (!hasText(value)) return "";
  const normalized = value.trim().toLowerCase().replace(/_/g, "-");
  if (normalized === "vegetarian") return "veg";
  if (normalized === "nonveg") return "non-veg";
  if (normalized === "both" || normalized === "veg" || normalized === "non-veg") {
    return normalized;
  }
  return value;
};

const mergeAutofillIntoForm = (prev, autofill = {}, serviceType = "") => {
  const nextForm = {
    ...prev,
    name: hasText(autofill.name) ? autofill.name : prev.name,
    description: hasText(autofill.descriptionHtml)
      ? autofill.descriptionHtml
      : prev.description,
    serviceAddress: {
      ...prev.serviceAddress,
      ...(autofill.serviceAddress || {}),
    },
    serviceLocation:
      autofill?.serviceLocation?.coordinates?.length === 2
        ? autofill.serviceLocation
        : prev.serviceLocation,
    contact_details: {
      ...prev.contact_details,
      ...(autofill.contact_details || {}),
    },
    paymentMethods: mergeUniqueStrings(
      prev.paymentMethods,
      autofill.paymentMethods
    ),
    subCategories: mergeUniqueStrings(prev.subCategories, autofill.subCategories),
    typeOfServices: mergeUniqueStrings(
      prev.typeOfServices,
      autofill.typeOfServices
    ),
    typeOfCuisine: mergeUniqueStrings(prev.typeOfCuisine, autofill.typeOfCuisine),
    amenities: mergeUniqueStrings(prev.amenities, autofill.amenities),
    accessibilityFeatures: mergeUniqueStrings(
      prev.accessibilityFeatures,
      autofill.accessibilityFeatures
    ),
    nearbyLandmarks: mergeUniqueStrings(
      prev.nearbyLandmarks,
      autofill.nearbyLandmarks
    ),
    foodType: normalizeFoodType(autofill.foodType) || prev.foodType,
    images: mergeImages(prev.images || [], autofill.images || []),
  };

  if (serviceType === "venue") {
    nextForm.venueType = hasText(autofill.venueType)
      ? autofill.venueType
      : prev.venueType;
    nextForm.areas_available = mergeAreas(
      prev.areas_available,
      autofill.areas_available
    );
    nextForm.venueSlots = mergeVenueSlots(prev.venueSlots, autofill.venueSlots);
    nextForm.cuisineAllowed = {
      ...prev.cuisineAllowed,
      ...(autofill.cuisineAllowed || {}),
      veg:
        typeof autofill?.cuisineAllowed?.veg === "boolean"
          ? autofill.cuisineAllowed.veg
          : prev?.cuisineAllowed?.veg,
      nonVeg:
        typeof autofill?.cuisineAllowed?.nonVeg === "boolean"
          ? autofill.cuisineAllowed.nonVeg
          : prev?.cuisineAllowed?.nonVeg,
    };
    nextForm.pricing = mergeVenuePricing(prev.pricing, autofill.pricing);
    nextForm.parking = {
      ...prev.parking,
      ...(autofill.parking || {}),
      available:
        typeof autofill?.parking?.available === "boolean"
          ? autofill.parking.available
          : prev?.parking?.available,
      capacity: toNumber(
        autofill?.parking?.capacity,
        prev?.parking?.capacity || 0
      ),
    };
    nextForm.smokingAllowed =
      typeof autofill?.smokingAllowed === "boolean"
        ? autofill.smokingAllowed
        : prev.smokingAllowed;
    nextForm.externalVendors = {
      ...prev.externalVendors,
      ...(autofill.externalVendors || {}),
    };
  }

  if (serviceType === "caterers") {
    nextForm.pricing = mergeCatererPricing(prev.pricing, autofill.pricing);
  }

  if (serviceType === "photography") {
    nextForm.pricing = mergePackageArray(prev.pricing, autofill.pricing, {
      packageName: "",
      price: 0,
      description: "",
      out_station: "no_travel",
    });
  }

  if (serviceType === "decors") {
    nextForm.pricing = mergePackageArray(prev.pricing, autofill.pricing, {
      packageName: "",
      price: 0,
      description: "",
      type: "basic",
    });
    nextForm.drone_shoot_available =
      typeof autofill?.drone_shoot_available === "boolean"
        ? autofill.drone_shoot_available
        : prev.drone_shoot_available;
  }

  return nextForm;
};

const VendorLiftAI = ({ serviceType, formData, setFormData }) => {
  const [loading, setLoading] = useState(false);
  const [insights, setInsights] = useState(null);
  const [sourceQuery, setSourceQuery] = useState("");

  const handleGenerate = async () => {
    if (!formData?.name?.trim()) {
      toast.error("Enter the service name before using VendorLift AI");
      return;
    }

    try {
      setLoading(true);
      const response = await api.post("/api/vendor/ai/listing-copilot", {
        serviceType,
        serviceData: formData,
      });

      const payload = response.data?.data;
      setInsights(payload);
      setFormData((prev) =>
        mergeAutofillIntoForm(
          {
            ...prev,
            description: payload?.listing?.descriptionHtml?.trim() || prev.description,
            images: mergeImages(
              prev.images || [],
              (payload?.photoSuggestions || []).map((photo) => ({
                url: photo.url,
                caption: photo.title || "Web listing photo",
              }))
            ),
          },
          payload?.autofill || {},
          serviceType
        )
      );

      toast.success("VendorLift AI updated the description, sections, and photo suggestions");
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Failed to generate AI listing suggestions"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleImportFromWeb = async () => {
    if (!sourceQuery.trim()) {
      toast.error("Enter a business name or listing query first");
      return;
    }

    try {
      setLoading(true);
      const response = await api.post("/api/vendor/ai/listing-copilot", {
        serviceType,
        serviceData: formData,
        sourceQuery,
      });

      const payload = response.data?.data;
      setInsights(payload);
      setFormData((prev) =>
        mergeAutofillIntoForm(prev, payload?.autofill || {}, serviceType)
      );
      toast.success("Imported web listing details into the form");
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Failed to import listing details from the web"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-2xl border border-gray-200 bg-gradient-to-br from-purple-50 via-white to-pink-50 p-4 space-y-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 px-3 py-1 text-xs font-semibold text-white">
            <PenTool className="h-3.5 w-3.5" />
            VendorLift AI
          </div>
          <h4 className="mt-3 text-base font-bold text-gray-900">
            Auto-write copy, browse the web for ideas, and score this listing
          </h4>
          <p className="mt-1 text-sm text-gray-600">
            Uses the current form details to draft marketplace copy and show what is still missing.
          </p>
        </div>

        <button
          type="button"
          onClick={handleGenerate}
          disabled={loading}
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 px-4 py-2.5 text-sm font-semibold text-white shadow-md shadow-purple-600/20 transition-all hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-60"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <PenTool className="h-4 w-4" />}
          {loading ? "Thinking..." : "Generate With VendorLift AI"}
        </button>
      </div>

      <div className="grid gap-3 md:grid-cols-[1fr_auto]">
        <input
          type="text"
          value={sourceQuery}
          onChange={(e) => setSourceQuery(e.target.value)}
          placeholder="Import from web: business name, Google Maps title, city..."
          className="w-full rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-800 outline-none transition focus:border-purple-400 focus:ring-4 focus:ring-purple-500/10"
        />
        <button
          type="button"
          onClick={handleImportFromWeb}
          disabled={loading}
          className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-800 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
          Import From Web
        </button>
      </div>

      {insights && (
        <div className="grid gap-4 lg:grid-cols-[220px_1fr]">
          <div className="space-y-3">
            <div className={`rounded-2xl border px-4 py-3 ${scoreClasses(insights?.profileScore?.score)}`}>
              <p className="text-xs font-semibold uppercase tracking-wide">Profile score</p>
              <div className="mt-2 flex items-end gap-2">
                <span className="text-3xl font-black">{insights?.profileScore?.score ?? 0}</span>
                <span className="pb-1 text-xs font-semibold uppercase">/ 100</span>
              </div>
              <p className="mt-1 text-sm font-semibold">{insights?.profileScore?.label}</p>
            </div>

            {Array.isArray(insights?.listing?.highlights) &&
              insights.listing.highlights.length > 0 && (
                <div className="rounded-2xl border border-gray-200/70 bg-white p-4 shadow-[0_1px_3px_rgba(16,24,40,0.04)]">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    AI highlights
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {insights.listing.highlights.slice(0, 6).map((item) => (
                      <span
                        key={item}
                        className="rounded-full bg-purple-50 px-2.5 py-1 text-xs font-medium text-purple-700"
                      >
                        {item}
                      </span>
                    ))}
                  </div>
                </div>
              )}
          </div>

          <div className="space-y-4">
            {Array.isArray(insights?.profileScore?.missingItems) &&
              insights.profileScore.missingItems.length > 0 && (
                <div className="rounded-2xl border border-gray-200/70 bg-white p-4 shadow-[0_1px_3px_rgba(16,24,40,0.04)]">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    What is missing
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {insights.profileScore.missingItems.map((item) => (
                      <span
                        key={item}
                        className="rounded-full bg-rose-50 px-2.5 py-1 text-xs font-medium text-rose-700"
                      >
                        {item}
                      </span>
                    ))}
                  </div>
                </div>
              )}

            {insights?.sourceSummary && (
              <div className="rounded-2xl border border-gray-200/70 bg-white p-4 shadow-[0_1px_3px_rgba(16,24,40,0.04)]">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Imported summary
                </p>
                <p className="mt-2 text-sm text-gray-700">{insights.sourceSummary}</p>
              </div>
            )}

            <div className="rounded-2xl border border-gray-200/70 bg-white p-4 shadow-[0_1px_3px_rgba(16,24,40,0.04)]">
              <div className="flex items-center gap-2">
                <Search className="h-4 w-4 text-gray-500" />
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Web photo searches
                </p>
              </div>
              {Array.isArray(insights?.browserSearches) &&
              insights.browserSearches.length > 0 ? (
                <div className="mt-3 flex flex-wrap gap-2">
                  {insights.browserSearches.map((link) => (
                    <a
                      key={link.url}
                      href={link.url}
                      target="_blank"
                      rel="noreferrer"
                      className="rounded-full border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 transition hover:border-gray-300 hover:bg-gray-50"
                    >
                      {link.label}
                    </a>
                  ))}
                </div>
              ) : (
                <p className="mt-2 text-sm text-gray-500">
                  No web search suggestions were returned yet.
                </p>
              )}
            </div>

            {Array.isArray(insights?.references) && insights.references.length > 0 && (
              <div className="rounded-2xl border border-gray-200/70 bg-white p-4 shadow-[0_1px_3px_rgba(16,24,40,0.04)]">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Live web references
                </p>
                <div className="mt-3 space-y-2">
                  {insights.references.slice(0, 4).map((reference) => (
                    <a
                      key={reference.url}
                      href={reference.url}
                      target="_blank"
                      rel="noreferrer"
                      className="block rounded-xl border border-gray-200 px-3 py-2 transition hover:bg-gray-50"
                    >
                      <p className="text-sm font-semibold text-gray-800">{reference.title}</p>
                      <p className="mt-1 text-xs text-gray-500">{reference.reason}</p>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorLiftAI;
